"use strict";
/**
 * @overview Raven implementation for sending errors caught client-side to Sentry.
 * @version 1.0.0
 * @author Garrett Wills
 */
const Sentry = require('@sentry/browser');
module.exports = {
    /**
     * Connects Raven to Sentry with the given configuration options if enabled.
     * @function config
     * @param {boolean} enabled - Boolean indicating wether Sentry is enabled.
     * @param {number} dsn - Connection string so that Raven can talk to Sentry.
     * @param {number} options - Configuration options.
     */
    config: (enabled, dsn, options) => {
        return enabled && Sentry.init(Object.assign({ dsn }, options));
    },
    /**
     * Wraps a server instance in the Raven context.
     * @function context
     * @param {Function} instance - The server instance to be wrapped.
     */
    context: (instance) => {
        return Sentry.context(instance);
    },
    /**
     * Captures breadcrumbs, which allow us to get a better picture for tracing back the path of the error.
     * @function captureBreadcrumb
     * @param {string} message - The message that will go along with the breadcrumb (in our case we use the function name).
     * @param {string} category - The category of the breadcrumb (console, exception, function, etc.)
     */
    captureBreadcrumb: (message, category) => {
        return Sentry.captureBreadcrumb({ message: message, category: category });
    },
    /**
     * Sets certain context information for the error being thrown (in our case we are using it to set the user.)
     * @function setUserContext
     * @param {string} id - The id of the user the error is associated with.
     * @param {string} username - The username of the user the error is associated with.
     * @param {string} email - The email of the user the error is associated with.
     */
    setUserContext: (id, username, email) => {
        return Sentry.setUserContext({ id: id, username: username, email: email });
    },
    /**
     * Sets certain context information for the error being thrown (in our case we are using it to set the user.)
     * @function mergeContext
     * @param {Object} data - The data that we wish to merge with the current context.
     */
    mergeContext: (data) => {
        return Sentry.mergeContext(data);
    },
    /**
     * Sends a caught exception to Sentry with the associated data.
     * @function captureException
     * @param {Error} error - The error being caught.
     * @param {string} data - Extra data that we want associated with the error (we send tags and a custom error message.)
     */
    captureException: (error, data) => {
        return Sentry.captureException(error, data);
    },
    /**
     * Sends a caught message to Sentry with the associated data.
     * @function captureMessage
     * @param {Error} message - The message being caught.
     * @param {string} data - Extra data that we want associated with the message.
     */
    captureMessage: (message, data) => {
        return Sentry.captureMessage(message, data);
    },
    /**
     * Returns a boolean indicating wether or not Sentry has been set up.
     * @function isSetup
     */
    isSetup: () => {
        return Sentry.isSetup();
    }
};



// WEBPACK FOOTER //
// ./~/@infiniscene/lib/sentry/client/index.js